import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { HeadingMedium, BodyText } from "../central/Typography";
import { useSeason } from "../context/SeasonContext";

export default function PlayerCardModal({
  open,
  onClose,
  onAddEdit,
  player,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  players = [],
  getEffectivePrice,
  formRank,
  leagueID,
  availablePlayerIDs = null,
}) {
  const [statMode, setStatMode] = useState("totals");
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  // Read-only from context — never write back to it from inside a modal
  const { availableSeasons, selectedSeasonID, currentSeasonID } = useSeason();
  // Local season state: initialised from context, fully isolated, discarded on close
  const [modalSeasonID, setModalSeasonID] = useState(selectedSeasonID);
  // Historical player data fetched when viewing a non-current season
  const [modalPlayerData, setModalPlayerData] = useState(null);

  // Reset local state every time the modal is freshly opened
  useEffect(() => {
    if (open) {
      setModalSeasonID(selectedSeasonID);
      setSeasonDropdownOpen(false);
      setModalPlayerData(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch historical player data when modalSeasonID differs from currentSeasonID
  useEffect(() => {
    if (!open || !player?.id || !leagueID) return;
    if (modalSeasonID === currentSeasonID) {
      setModalPlayerData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "leagues", leagueID, "seasons", modalSeasonID, "players", player.id));
        if (!cancelled) setModalPlayerData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch {
        if (!cancelled) setModalPlayerData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [modalSeasonID, player?.id, leagueID, open, currentSeasonID]); // eslint-disable-line react-hooks/exhaustive-deps

  // All data display uses displayPlayer; falls back to the prop when on current season
  const displayPlayer = modalPlayerData || player;

  const stats = displayPlayer?.stats || {};
  const selectionStats = displayPlayer?.selectionStats || {};

  const getLast3Form = (inputPlayer) => {
    const history = inputPlayer?.pointsHistory || {};
    const gwKeys = Object.keys(history).sort((a, b) => {
      const aNum = Number(String(a).replace("GW", "")) || 0;
      const bNum = Number(String(b).replace("GW", "")) || 0;
      return aNum - bNum;
    });
    return gwKeys.slice(-3).map((gw) => Number(history[gw]) || 0);
  };

  const getHistoryRows = (historyMap = {}) => {
    return Object.keys(historyMap)
      .sort((a, b) => {
        const aNum = Number(String(a).replace("GW", "")) || 0;
        const bNum = Number(String(b).replace("GW", "")) || 0;
        return bNum - aNum;
      })
      .map((gw) => ({ gw, value: historyMap[gw] }));
  };

  const pointsHistoryRows = useMemo(
    () => getHistoryRows(displayPlayer?.pointsHistory || {}),
    [displayPlayer] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const pointsHistoryChartRows = useMemo(() => {
    return Object.keys(displayPlayer?.pointsHistory || {})
      .sort((a, b) => {
        const aNum = Number(String(a).replace("GW", "")) || 0;
        const bNum = Number(String(b).replace("GW", "")) || 0;
        return aNum - bNum;
      })
      .map((gw) => ({ gw, value: Number(displayPlayer?.pointsHistory?.[gw] || 0) }));
  }, [displayPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  const priceHistoryRows = useMemo(
    () => getHistoryRows(displayPlayer?.priceHistory || {}),
    [displayPlayer] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const formatSignedMoney = (value) => {
    const num = Number(value || 0);
    return `${num >= 0 ? "+" : "-"}$${Math.abs(num).toFixed(1)}m`;
  };

  const getPerAppearance = (value) => {
    const num = Number(value) || 0;
    const apps = Number(stats.appearances) || 0;
    if (!apps) return 0;
    return Number((num / apps).toFixed(2));
  };

  const getPerAppearanceForPlayer = (inputPlayer, value) => {
    const num = Number(value) || 0;
    const apps = Number(inputPlayer?.stats?.appearances) || 0;
    if (!apps) return 0;
    return Number((num / apps).toFixed(2));
  };

  const getStatValueForPlayer = (inputPlayer, label, mode) => {
    const inputStats = inputPlayer?.stats || {};

    const totalsMap = {
      Points: Number(inputStats.totalPoints || 0),
      Appearances: Number(inputStats.appearances || 0),
      Starts: Number(inputStats.starts || 0),
      Goals: Number(inputStats.goals || 0),
      Assists: Number(inputStats.assists || 0),
      "Clean Sheets": Number(inputStats.cleanSheets || 0),
      Saves: Number(inputStats.saves || 0),
      Bonus: Number(inputStats.bonus || 0),
      "Yellow Cards": Number(inputStats.yellowCards || 0),
    };

    const totalValue = totalsMap[label] ?? 0;
    if (mode === "totals") return totalValue;
    if (label === "Appearances") return 1;
    return getPerAppearanceForPlayer(inputPlayer, totalValue);
  };

  const getRankForStat = (label, mode) => {
    if (!player || !players.length) return null;

    const ranked = [...players]
      .map((poolPlayer) => ({
        id: poolPlayer.id,
        value: getStatValueForPlayer(poolPlayer, label, mode),
      }))
      .sort((a, b) => b.value - a.value);

    let currentRank = 1;
    for (let i = 0; i < ranked.length; i += 1) {
      if (i > 0 && ranked[i].value < ranked[i - 1].value) {
        currentRank = i + 1;
      }
      if (ranked[i].id === player.id) {
        return currentRank;
      }
    }

    return null;
  };

  const isKeeperStyle = Number(stats.saves || 0) > 0;
  const currentPrice = modalPlayerData
    ? Number(modalPlayerData.currentPrice || 0)
    : Number(getEffectivePrice?.(player, "current") || player?.currentPrice || 0);
  const last3Form = getLast3Form(displayPlayer);
  const avgPointsPerAppPlayer = (() => {
    const totalPoints = Number(stats.totalPoints || 0);
    const totalApps = Number(stats.appearances || 0);
    if (!totalApps) return 0;
    return totalPoints / totalApps;
  })();

  const totalPriceChange = (() => {
    const rows = getHistoryRows(displayPlayer?.priceHistory || {});
    if (!rows.length) return 0;
    const oldest = Number(rows[rows.length - 1]?.value || 0);
    return Number((currentPrice - oldest).toFixed(1));
  })();

  const statRows = [
    {
      label: "Points",
      total: Number(stats.totalPoints || 0),
      perApp: getPerAppearance(stats.totalPoints),
    },
    {
      label: "Appearances",
      total: Number(stats.appearances || 0),
      perApp: 1,
    },
    {
      label: "Starts",
      total: Number(stats.starts || 0),
      perApp: getPerAppearance(stats.starts),
    },
    {
      label: "Goals",
      total: Number(stats.goals || 0),
      perApp: getPerAppearance(stats.goals),
    },
    {
      label: "Assists",
      total: Number(stats.assists || 0),
      perApp: getPerAppearance(stats.assists),
    },
    {
      label: "Clean Sheets",
      total: Number(stats.cleanSheets || 0),
      perApp: getPerAppearance(stats.cleanSheets),
    },
    ...(isKeeperStyle
      ? [
          {
            label: "Saves",
            total: Number(stats.saves || 0),
            perApp: getPerAppearance(stats.saves),
          },
        ]
      : []),
    {
      label: "Bonus",
      total: Number(stats.bonus || 0),
      perApp: getPerAppearance(stats.bonus),
    },
    {
      label: "Yellow Cards",
      total: Number(stats.yellowCards || 0),
      perApp: getPerAppearance(stats.yellowCards),
    },
  ];

  const sheetRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchCurrentYRef = useRef(null);
  const dragEnabledRef = useRef(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);

  const handleTouchStart = (e) => {
    const sheetEl = sheetRef.current;
    const startY = e.touches?.[0]?.clientY ?? null;
    touchStartYRef.current = startY;
    touchCurrentYRef.current = startY;
    dragEnabledRef.current = !sheetEl || sheetEl.scrollTop <= 0;
  };

  const handleTouchMove = (e) => {
    if (!dragEnabledRef.current) return;

    const currentY = e.touches?.[0]?.clientY ?? null;
    touchCurrentYRef.current = currentY;

    const startY = touchStartYRef.current;
    if (typeof startY !== "number" || typeof currentY !== "number") return;

    const deltaY = currentY - startY;
    if (deltaY <= 0) {
      setDragOffsetY(0);
      return;
    }

    if (typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    setIsDraggingSheet(true);
    setDragOffsetY(deltaY);
  };

  const handleTouchEnd = () => {
    const startY = touchStartYRef.current;
    const endY = touchCurrentYRef.current;
    const deltaY =
      typeof startY === "number" && typeof endY === "number"
        ? endY - startY
        : 0;

    const shouldClose = dragEnabledRef.current && deltaY > 110;

    touchStartYRef.current = null;
    touchCurrentYRef.current = null;
    dragEnabledRef.current = false;
    setIsDraggingSheet(false);

    if (shouldClose) {
      setDragOffsetY(0);
      onClose?.();
      return;
    }

    setDragOffsetY(0);
  };

  if (!open || !player) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overscrollBehavior: "contain",
      }}
    >
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: "100%",
          maxWidth: "560px",
          maxHeight: "84vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          background: "#fff",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "1rem 1rem 1.15rem",
          boxSizing: "border-box",
          boxShadow: "0 -10px 30px rgba(0,0,0,0.2)",
          transform: `translateY(${dragOffsetY}px)`,
          transition: isDraggingSheet ? "none" : "transform 180ms ease-out",
          WebkitOverflowScrolling: "touch",
          willChange: "transform",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: "44px",
            height: "5px",
            borderRadius: "999px",
            background: "#d1d5db",
            margin: "0 auto 0.85rem",
          }}
        />

        {/* Top row: Season pill + Done */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.65rem",
          }}
        >
          {availableSeasons.length > 1 && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setSeasonDropdownOpen((v) => !v)}
                style={{
                  border: "1px solid #d9e0e6",
                  borderRadius: "999px",
                  padding: "0.18rem 0.55rem",
                  background: "#f4f7f8",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#555",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  lineHeight: 1.4,
                }}
              >
                {modalSeasonID} ▾
              </button>
              {seasonDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 4px)",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "0.35rem",
                    zIndex: 10,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    minWidth: "72px",
                  }}
                >
                  {availableSeasons.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setModalSeasonID(s);
                        setSeasonDropdownOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        background: s === modalSeasonID ? "#50B4C8" : "transparent",
                        color: s === modalSeasonID ? "#fff" : "#234",
                        border: "none",
                        borderRadius: "8px",
                        padding: "0.3rem 0.6rem",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        textAlign: "center",
                        fontFamily: "inherit",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "0.95rem",
              fontWeight: "600",
              color: "#555",
              cursor: "pointer",
              padding: 0,
              lineHeight: 1,
            }}
          >
            Done
          </button>
        </div>

        {/* Player name + subtitle + badges */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.9rem" }}>
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            aria-label="Previous player"
            style={{ background: "none", border: "none", fontSize: "1.2rem", fontWeight: "700", color: canPrev ? "#50B4C8" : "#ddd", cursor: canPrev ? "pointer" : "default", padding: "0.2rem 0.3rem", lineHeight: 1, flexShrink: 0 }}
          >‹</button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <HeadingMedium style={{ margin: 0, fontSize: "1.25rem" }}>
              {player.name}
            </HeadingMedium>
            <BodyText style={{ margin: "0.2rem 0 0", fontSize: "0.86rem", color: "#666" }}>
              {player.position} • ${currentPrice.toFixed(1)}m • Form Rank #{formRank || "-"}
            </BodyText>
            {availablePlayerIDs?.has(player.id) && (
              <div style={{ marginTop: 6 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: "#e8f5e9", color: "#2e7d32",
                  fontSize: "0.75rem", fontWeight: 700,
                  padding: "2px 8px", borderRadius: 20,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: "#4CAF50", display: "inline-block",
                  }} />
                  Available this GW
                </span>
              </div>
            )}
            {modalSeasonID !== currentSeasonID && availableSeasons.length > 1 && (
              <BodyText style={{ margin: "0.15rem 0 0", fontSize: "0.75rem", color: "#999" }}>
                Viewing {modalSeasonID} data
              </BodyText>
            )}
            <div style={{ marginTop: "0.45rem" }}>
              <button
                type="button"
                onClick={() => onAddEdit?.(player)}
                style={{
                  border: "none",
                  borderRadius: "999px",
                  padding: "0.22rem 0.68rem",
                  background: "#E07856",
                  color: "#fff",
                  fontSize: "0.82rem",
                  fontWeight: "700",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Add / Edit
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            aria-label="Next player"
            style={{ background: "none", border: "none", fontSize: "1.2rem", fontWeight: "700", color: canNext ? "#50B4C8" : "#ddd", cursor: canNext ? "pointer" : "default", padding: "0.2rem 0.3rem", lineHeight: 1, flexShrink: 0 }}
          >›</button>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.9rem",
          }}
        >
          <div
            style={{
              background: "#f4f7f8",
              borderRadius: "14px",
              border: "1px solid #e5e7eb",
              padding: "0.65rem 0.8rem",
              minWidth: "115px",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              Total Points
            </BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "1rem", fontWeight: "700", color: "#111" }}>
              {Number(stats.totalPoints || 0)}
            </BodyText>
          </div>

          <div
            style={{
              background: "#f4f7f8",
              borderRadius: "14px",
              padding: "0.65rem 0.8rem",
              border: "1px solid #e5e7eb",
              minWidth: "116px",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              Last 3 GW
            </BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "1rem", fontWeight: "700", color: "#111" }}>
              {last3Form.length ? [...last3Form].reverse().join(" • ") : "—"}
            </BodyText>
          </div>

          <div
            style={{
              background: "#f4f7f8",
              borderRadius: "14px",
              padding: "0.65rem 0.8rem",
              border: "1px solid #e5e7eb",
              minWidth: "115px",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              Picked By
            </BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "1rem", fontWeight: "700", color: "#111" }}>
              {Number(selectionStats.selectedByTeams || 0)} ({Number(selectionStats.selectedPct || 0)}%)
            </BodyText>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.45rem",
            marginBottom: "0.9rem",
          }}
        >
          <button
            type="button"
            onClick={() => setStatMode("totals")}
            style={{
              border: "none",
              borderRadius: "999px",
              padding: "0.48rem 0.9rem",
              fontSize: "0.85rem",
              fontWeight: "700",
              background: statMode === "totals" ? "#50B4C8" : "#eef2f4",
              color: statMode === "totals" ? "#fff" : "#234",
              cursor: "pointer",
            }}
          >
            Totals
          </button>
          <button
            type="button"
            onClick={() => setStatMode("perApp")}
            style={{
              border: "none",
              borderRadius: "999px",
              padding: "0.48rem 0.9rem",
              fontSize: "0.85rem",
              fontWeight: "700",
              background: statMode === "perApp" ? "#50B4C8" : "#eef2f4",
              color: statMode === "perApp" ? "#fff" : "#234",
              cursor: "pointer",
            }}
          >
            Per Appearance
          </button>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "16px",
            overflow: "hidden",
            marginBottom: "0.9rem",
          }}
        >
          {statRows
            .filter((row) => !(statMode === "perApp" && row.label === "Appearances"))
            .map((row, index) => (
            <div
              key={row.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.78rem 0.9rem",
                background: index % 2 === 0 ? "#fff" : "#fafbfc",
                borderTop: index === 0 ? "none" : "1px solid #eef2f4",
              }}
            >
              <BodyText style={{ margin: 0, fontSize: "0.88rem", color: "#234", fontWeight: "600" }}>
                {row.label}
              </BodyText>
              <BodyText style={{ margin: 0, fontSize: "0.92rem", color: "#111", fontWeight: "700" }}>
                {statMode === "totals" ? row.total : row.perApp}
                <span style={{ fontStyle: "italic", color: "#666" }}>
                  {row.label === "Clean Sheets"
                    ? ""
                    : ` • #${getRankForStat(row.label, statMode) || "-"}`}
                </span>
              </BodyText>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            marginBottom: "0.9rem",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "0.65rem 0.7rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              Price Δ (Season)
            </BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "0.98rem", fontWeight: "700", color: totalPriceChange >= 0 ? "#1f7a3e" : "#d32f2f" }}>
              {formatSignedMoney(totalPriceChange)}
            </BodyText>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "0.65rem 0.7rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
            Price Δ (GW)
            </BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "0.98rem", fontWeight: "700", color: Number(displayPlayer?.priceDelta || 0) >= 0 ? "#1f7a3e" : "#d32f2f" }}>
              {formatSignedMoney(displayPlayer?.priceDelta || 0)}
            </BodyText>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "0.65rem 0.7rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              Price Trend
            </BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "0.98rem",
                fontWeight: "700",
                color:
                  Number(displayPlayer?.priceAccumulator || 0) > 0
                    ? "#1f7a3e"
                    : Number(displayPlayer?.priceAccumulator || 0) < 0
                      ? "#d32f2f"
                      : "#111",
              }}
            >
              {`${Math.round(Number(displayPlayer?.priceAccumulator || 0) * 1000)}%`}
            </BodyText>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            padding: "0.75rem 0.8rem",
            marginBottom: "0.9rem",
            background: "#fff",
          }}
        >
          <BodyText style={{ margin: 0, fontSize: "0.8rem", fontWeight: "700", color: "#111" }}>
            Points by GW
          </BodyText>

          {pointsHistoryChartRows.length ? (
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "flex-end",
                gap: "0.45rem",
                height: "140px",
                marginTop: "0.75rem",
                overflowX: "auto",
                paddingBottom: "0.2rem",
              }}
            >
              {(() => {
                const maxValue = Math.max(...pointsHistoryChartRows.map((row) => row.value), 1);
                const avgHeight = maxValue ? (avgPointsPerAppPlayer / maxValue) * 92 : 0;

                return (
                  <>
                    <>
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: `${Math.max(avgHeight, 6)}px`,
                          borderTop: "2px dotted #999",
                          opacity: 0.8,
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          right: 0,
                          bottom: `${Math.max(avgHeight, 6) + 4}px`,
                          fontSize: "0.68rem",
                          color: "#666",
                          fontStyle: "italic",
                          background: "rgba(255,255,255,0.9)",
                          padding: "0 0.2rem",
                          lineHeight: 1,
                        }}
                      >
                        {`${avgPointsPerAppPlayer.toFixed(1)} Avg`}
                      </div>
                    </>

                    {pointsHistoryChartRows.map((row) => {
                      const barHeight = Math.max((row.value / maxValue) * 92, row.value > 0 ? 12 : 6);
                      return (
                        <div
                          key={row.gw}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            minWidth: "34px",
                            flex: "0 0 auto",
                            height: "100%",
                          }}
                        >
                          <BodyText
                            style={{
                              margin: "0 0 0.25rem",
                              fontSize: "0.72rem",
                              fontWeight: "700",
                              color: "#234",
                              lineHeight: 1,
                            }}
                          >
                            {row.value}
                          </BodyText>
                          <div
                            style={{
                              width: "24px",
                              height: `${barHeight}px`,
                              borderRadius: "8px 8px 4px 4px",
                              background: "#50B4C8",
                            }}
                          />
                          <BodyText
                            style={{
                              margin: "0.35rem 0 0",
                              fontSize: "0.7rem",
                              color: "#666",
                              lineHeight: 1,
                            }}
                          >
                            {row.gw}
                          </BodyText>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          ) : (
            <BodyText style={{ margin: "0.6rem 0 0", fontSize: "0.8rem", color: "#666" }}>
              No points data yet
            </BodyText>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.75rem",
            marginBottom: "0.9rem",
          }}
        >
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "0.65rem 0.8rem",
              minWidth: "115px",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.8rem", fontWeight: "700", color: "#111" }}>
              Points History
            </BodyText>
            <div style={{ marginTop: "0.55rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              {pointsHistoryRows.length ? (
                pointsHistoryRows.map((row) => (
                  <div
                    key={row.gw}
                    style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}
                  >
                    <BodyText style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>{row.gw}</BodyText>
                    <BodyText style={{ margin: 0, fontSize: "0.82rem", fontWeight: "700", color: "#111" }}>{Number(row.value || 0)}</BodyText>
                  </div>
                ))
              ) : (
                <BodyText style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>No data</BodyText>
              )}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "0.75rem 0.8rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.8rem", fontWeight: "700", color: "#111" }}>
              Price History
            </BodyText>
            <div style={{ marginTop: "0.55rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              {priceHistoryRows.length ? (
                priceHistoryRows.map((row) => (
                  <div
                    key={row.gw}
                    style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}
                  >
                    <BodyText style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>{row.gw}</BodyText>
                    <BodyText style={{ margin: 0, fontSize: "0.82rem", fontWeight: "700", color: "#111" }}>${Number(row.value || 0).toFixed(1)}m</BodyText>
                  </div>
                ))
              ) : (
                <BodyText style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>No data</BodyText>
              )}
            </div>
          </div>
        </div>

        {/* Bottom prev / next navigation */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1.5rem",
            paddingTop: "0.6rem",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            style={{
              background: "none",
              border: "none",
              fontSize: "0.9rem",
              fontWeight: "700",
              color: canPrev ? "#50B4C8" : "#ccc",
              cursor: canPrev ? "pointer" : "default",
              padding: "0.3rem 0.5rem",
              fontFamily: "inherit",
            }}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            style={{
              background: "none",
              border: "none",
              fontSize: "0.9rem",
              fontWeight: "700",
              color: canNext ? "#50B4C8" : "#ccc",
              cursor: canNext ? "pointer" : "default",
              padding: "0.3rem 0.5rem",
              fontFamily: "inherit",
            }}
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
